import { Injectable } from '@angular/core';
import { IntakeForm } from 'src/API';
import { isWithinTimeFrameInDays } from 'src/app/core/api/date.util';
import { safeJsonParse } from 'src/app/core/api/general.util';
import { medicalHistoryCustomAutopopulateConfig } from 'src/app/encounters/encounter-linking/medical-history-linking';
import { AutopopulateService } from 'src/app/shared/autopopulate/autopopulate.service';
import { DryEyeMedicalHistoryFormGroup } from 'src/app/shared/consult-forms/dry-eye-forms/dry-eye-medical-history-form/dry-eye-medical-history-form.model';
import { splitMedicationFreeText } from '../medication/medication-free-text.util';
import { MedicalHistoryPopulator } from '../medical-history-source.model';

/**
 * Applies an intake form response. Config-driven intake forms go through the
 * autopopulate engine; legacy forms only carry free-text medications.
 */
@Injectable({ providedIn: 'root' })
export class IntakeFormPopulator implements MedicalHistoryPopulator {
  constructor(private autopopulateService: AutopopulateService) {}

  populate(formGroup: DryEyeMedicalHistoryFormGroup, intakeForm: IntakeForm): void {
    // Only populate with intake form if it was completed within 1 year.
    if (!intakeForm || !isWithinTimeFrameInDays(new Date(intakeForm.createdAt), new Date(), 365)) {
      return;
    }
    const response = safeJsonParse(intakeForm.response);
    if (!response || typeof response !== 'object') {
      return;
    }
    if (intakeForm.config) {
      this.autopopulateService.populateFormGroup(
        formGroup,
        medicalHistoryCustomAutopopulateConfig,
        response['answers'],
        false
      );
      return;
    }

    const patientGeneralHealthQuestion = response.patientGeneralHealthQuestion;
    const intakeFormMedications =
      patientGeneralHealthQuestion && patientGeneralHealthQuestion.medications;
    if (patientGeneralHealthQuestion && intakeFormMedications) {
      // Merge into any medications already staged by earlier populators (e.g.
      // DERFS) instead of overwriting, so nothing is lost before the
      // MedicationReconciler folds them into `medications`.
      const currentFreeText = formGroup.controls.patientEnteredMedicationsFreeText.value;
      formGroup.controls.patientEnteredMedicationsFreeText.patchValue([
        ...splitMedicationFreeText(currentFreeText),
        ...splitMedicationFreeText(intakeFormMedications)
      ]);
    }
  }
}
